import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { VerbrauchstypPipe } from '../../../pipes/verbrauchstyp.pipe';
import { TRACKING } from '../../../enums/trackingParts.enum';
import { VERBRAUCHSTYP } from '../../../enums/verbrauchstyp.enum';
import { ALLOWED_KEYS, StorageService } from '../../../services/storage.service';
import { TrackingService } from '../../../services/tracking.service';
import { TrackingPixelData } from '../../../models/trackingPixelData';
import { Subscription } from 'rxjs';
import { KameleoonService } from '../../../services/kameleoon.service';
import { ContractDetails, TariffData } from '../../../../../assets/js/com/ts_api_client';
import { StoragePersonalData } from '../../../models/storagePersonalData';
import { TARIFF_CONTEXT } from '../../../enums/tariffContext.enum';

@Component({
    selector: 'bdo-customer-status',
    templateUrl: './customer-status.component.html',
    styleUrls: ['./customer-status.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CustomerStatusComponent implements OnInit, OnDestroy {
  public TRACKING = TRACKING;
  private tariffData: Array<TariffData> = [];
  private personalData: StoragePersonalData;
  private trackingPixelData: TrackingPixelData;
  private subscriptions = new Subscription();

  constructor(
    public kameleoonService: KameleoonService,
    private trackingService: TrackingService,
    private route: ActivatedRoute,
    private verbrauchstypPipe: VerbrauchstypPipe,
    private router: Router
  ) { }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit() {
    // If landing on customerstatus we clear any remaining offer infos from storage
    StorageService.clearOfferContext();
    StorageService.clearOfferId();

    const oldTitles = ['Umweltliebende', 'Umweltbewusste', 'Freiheitsliebende', 'Sicherheitsorientierte', 'Heimatliebende'];

    // Save Params from TariffAdvisor in model
    this.subscriptions.add(this.route.queryParamMap.subscribe(
      { next: res => {
        const chargingConsumption = parseInt(res.get('chargingConsumption'), 10);
        const annualConsumption = parseInt(res.get('AnnualConsumption'), 10);

        this.tariffData.push({
          postCode: res.get('zip'), // will be mapped to postCode
          powerModId: res.get('ext-id'),
          powerModBegin: res.get('begin'),
          hash: res.get('hash'),
          // We sum the consumptions as the chargingConsumption needs to be calculated for the price as well
          // and only gets substracted later with the bonus
          annualConsumption: (chargingConsumption ? (annualConsumption + chargingConsumption) : annualConsumption).toString(),
          type: res.get('type') as undefined,
          mode: res.get('mode') as undefined,
          divisionId: this.mapTypeToDivisionId(res.get('type')),
          productId: res.get('product-e') || res.get('product-g'),
          tariff: res.get('tariff-e') || res.get('tariff-g'),
          tbTariffName: res.get('bundletitle-e') || res.get('bundletitle-e'),
          annualConsumptionNt: res.get('AnnualConsumptionNt') || null
        });

        if (chargingConsumption) {
          StorageService.setChargeFlexData({
            chargingConsumption: chargingConsumption,
            carmodel: res.get('carmodel'),
            manufacturer: res.get('carmanufacturer'),
            isChargeFlexContext: true
          });
        }

        if (oldTitles.indexOf(this.tariffData[0].tbTariffName) >= 0) {
          this.tariffData[0].tbTariffName = 'Tarif für ' + this.tariffData[0].tbTariffName;
        }

        StorageService.setTariffData(this.tariffData);
        this.trackingService.postModeAndTypeOnEnterTracking(this.tariffData[0].mode, this.verbrauchstypPipe.transform(this.tariffData[0].divisionId, false));

        this.trackingPixelData = new TrackingPixelData(
          res.get('re_channel'),
          res.get('re_partnerid'),
          res.get('s_id'),
          res.get('ad_start')
        );
        StorageService.setValue(ALLOWED_KEYS.TRACKINGPIXEL_DATA, this.trackingPixelData);

        this.personalData = {
          streetIsChangeable: res.get('street') === '' || res.get('street') === null,
          housenumIsChangeable: res.get('house') === '' || res.get('house') === null,
          addressData:{
            street: res.get('street'),
            housenumber: res.get('house'),
            postCode: res.get('zip'),
            city: res.get('city')
          }
        };
        StorageService.setPersonalData(this.personalData);

        // Skipping the page as we should be new customer
        if (res.get('skip') === 'true') {
          this.router.navigate(['/vertrag-abschliessen/neu'], { replaceUrl: true });
        }

        // Directly jumping to contract change for existing customer (DeepLink may need Login first)
        if (res.get('accountId')?.length > 0 && res.get('contractId')?.length > 0) {
          // Save data needed
          const oldContract: ContractDetails = {
            contractId: res.get('contractId')
          };
          StorageService.setOldContract(oldContract);
          StorageService.setTariffContext(TARIFF_CONTEXT.CHANGE_PREFILLED);

          this.router.navigate(['/vertrag/' + res.get('accountId') + '/anpassen/tarifauswahl'], { replaceUrl: true });
        }
      } }
    ));
    this.subscriptions.add(this.router.events.subscribe(
      { next: event => {
        if (event instanceof NavigationStart && event.url === '/vertrag-abschliessen/neu') {
          StorageService.setTariffContext(TARIFF_CONTEXT.NEW_CUSTOMER);
          this.kameleoonService.processConversion(KameleoonService.Goals.DELIVERY_NEW_HERE);
        }
      } }
    ));
  }

  mapTypeToDivisionId(type: string): VERBRAUCHSTYP {
    let divisionId = null;
    switch (type) {
      case 'e':
        divisionId = VERBRAUCHSTYP.Strom;
        break;
      case 'g':
        divisionId = VERBRAUCHSTYP.Gas;
        break;
      default:
        break;
    }
    return divisionId;
  }
}
